import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import authService from '../services/authService';
import '../styles/login.css';

function LoginPage() {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSignupMode, setIsSignupMode] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  useEffect(() => {
    // Check for OAuth errors in URL first (before auth check)
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get('error');

    if (errorParam === 'oauth_failed') {
      setError('OAuth initialization failed. Please try again.');
    } else if (errorParam === 'auth_failed') {
      setError('Authentication failed. Please try again.');
    }

    // Check if user is already authenticated (async)
    const checkAuth = async () => {
      try {
        const isAuth = await authService.isAuthenticated();
        if (isAuth) {
          // Redirect to app with loop prevention
          const lastRedirect = sessionStorage.getItem('nalivox_last_redirect');
          const now = Date.now();
          if (!lastRedirect || (now - parseInt(lastRedirect)) > 2000) {
            sessionStorage.setItem('nalivox_last_redirect', now.toString());
            window.location.href = '/app';
          }
        }
      } catch (error) {
        console.error('Auth check error:', error);
      }
    };

    // Only check auth if no error (prevents redirect loop on auth failure)
    if (!errorParam) {
      checkAuth();
    }
  }, []);

  const handleGoogleLogin = () => {
    setIsLoading(true);
    authService.loginWithGoogle();
  };

  const handleGithubLogin = () => {
    setIsLoading(true);
    authService.loginWithGithub();
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user types
    if (error) setError('');
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      let result;
      if (isSignupMode) {
        // Signup
        if (!formData.name) {
          setError('Name is required');
          setIsLoading(false);
          return;
        }
        result = await authService.signup(formData.email, formData.password, formData.name);
      } else {
        // Login
        result = await authService.login(formData.email, formData.password);
      }

      if (result.success) {
        // Redirect to app
        window.location.href = '/app';
      } else {
        setError(result.error || 'Authentication failed');
        setIsLoading(false);
      }
    } catch (err) {
      setError('An unexpected error occurred');
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setIsSignupMode(!isSignupMode);
    setError('');
    setFormData({ email: '', password: '', name: '' });
  };

  return (
    <div className="login-container">
      {/* Background gradient */}
      <div className="login-bg-gradient"></div>

      {/* Animated particles */}
      <div className="login-particles">
        {[...Array(20)].map((_, i) => (
          <div key={i} className="particle" style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${5 + Math.random() * 10}s`
          }}></div>
        ))}
      </div>

      <div className="login-content">
        {/* Logo and brand */}
        <motion.div
          className="login-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="login-logo">
            {/* Favicon icon */}
            <svg viewBox="0 0 64 64" width="64" height="64" style={{ marginRight: '16px' }}>
              <rect x="2" y="2" width="60" height="60" rx="12" fill="#1F2937"/>
              <rect x="12" y="28" width="4" height="8" rx="2" fill="#10B981"/>
              <rect x="19" y="22" width="4" height="20" rx="2" fill="#10B981"/>
              <rect x="26" y="18" width="4" height="28" rx="2" fill="#10B981"/>
              <rect x="33" y="24" width="4" height="16" rx="2" fill="#10B981"/>
              <rect x="40" y="20" width="4" height="24" rx="2" fill="#10B981"/>
              <rect x="47" y="26" width="4" height="12" rx="2" fill="#10B981"/>
              <circle cx="54" cy="12" r="5" fill="#10B981"/>
              <path d="M54 9 v6 M51 12 h6" stroke="#1F2937" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            {/* Wordmark */}
            <svg viewBox="0 0 200 50" height="50" fill="none">
              <defs>
                <linearGradient id="loginLogoGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#00f0ff"/>
                  <stop offset="100%" stopColor="#00d4e0"/>
                </linearGradient>
                <linearGradient id="loginAccentGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#ff00aa"/>
                  <stop offset="100%" stopColor="#ff66cc"/>
                </linearGradient>
              </defs>
              <text x="0" y="38" fontFamily="system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" fontSize="40" fontWeight="700" letterSpacing="-1">
                <tspan fill="url(#loginLogoGradient)">NaLi</tspan><tspan fill="url(#loginAccentGradient)">Vox</tspan>
              </text>
            </svg>
          </div>
          <p className="login-subtitle">Continue to transcribe your meetings</p>
        </motion.div>

        {/* Login card */}
        <motion.div
          className="login-card gradient-border"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h2 className="login-card-title">
            {isSignupMode ? 'Create your account' : 'Sign in to your account'}
          </h2>

          {error && (
            <motion.div
              className="login-error"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              {error}
            </motion.div>
          )}

          <div className="login-buttons">
            <button
              className="login-btn login-btn-google"
              onClick={handleGoogleLogin}
              disabled={isLoading}
            >
              <svg className="login-btn-icon" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <button
              className="login-btn login-btn-github"
              onClick={handleGithubLogin}
              disabled={isLoading}
            >
              <svg className="login-btn-icon" viewBox="0 0 24 24">
                <path fill="currentColor" d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.17 6.839 9.49.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.463-1.11-1.463-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.831.092-.646.35-1.086.636-1.336-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.167 22 16.418 22 12c0-5.523-4.477-10-10-10z"/>
              </svg>
              Continue with GitHub
            </button>
          </div>

          <div className="login-divider">
            <span>or</span>
          </div>

          <form onSubmit={handleEmailAuth} className="login-form">
            {isSignupMode && (
              <div className="login-input-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your name"
                  required
                  disabled={isLoading}
                />
              </div>
            )}

            <div className="login-input-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                required
                disabled={isLoading}
              />
            </div>

            <div className="login-input-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder={isSignupMode ? "Minimum 8 characters" : "Enter your password"}
                required
                disabled={isLoading}
                minLength={isSignupMode ? 8 : undefined}
              />
            </div>

            <button
              type="submit"
              className="login-btn login-btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Please wait...' : (isSignupMode ? 'Sign Up' : 'Sign In')}
            </button>
          </form>

          <div className="login-toggle">
            {isSignupMode ? (
              <p>
                Already have an account?{' '}
                <button onClick={toggleMode} className="login-toggle-link" disabled={isLoading}>
                  Sign in
                </button>
              </p>
            ) : (
              <p>
                Don't have an account?{' '}
                <button onClick={toggleMode} className="login-toggle-link" disabled={isLoading}>
                  Sign up
                </button>
              </p>
            )}
          </div>

          <p className="login-terms">
            By signing in, you agree to our Terms of Service and Privacy Policy
          </p>
        </motion.div>

        {/* Back to home */}
        <motion.div
          className="login-footer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <a href="/" className="login-back-link">
            Back to Home
          </a>
        </motion.div>
      </div>
    </div>
  );
}

export default LoginPage;
